import * as React from "react";
import { Plus, StickyNote } from "lucide-react";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { NoteItem } from "@/modules/snapshots/components/NoteItem";
import { useNoteCreation } from "@/modules/snapshots/components/useNoteCreation";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
}

export const BusinessReportOutcomeNotes: React.FC<Props> = ({ businessReportOutcome }) => {
  const { notes, isAdding, toggleIsAdding, newNoteContent, setNewNoteContent, isCreating, handleAddNote, resetForm } =
    useNoteCreation(businessReportOutcome);

  return (
    <div className="mt-4 rounded-lg border border-[#ddd5cb] bg-[#fffdfb] px-5 py-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex size-8 items-center justify-center rounded-md bg-[#f1e2ce] text-desert-sand-700">
            <StickyNote className="size-4" aria-hidden="true" />
          </span>
          <h3 className="text-base font-semibold text-[#111b35]">Notes</h3>
        </div>
        {!isAdding && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5"
            onClick={() => toggleIsAdding(true)}
          >
            <Plus className="size-3.5" />
            Add note
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {notes.map(note => (
          <NoteItem key={note.id} note={note} outcomeId={businessReportOutcome.id} />
        ))}
        {isAdding && (
          <div>
            <Textarea
              value={newNoteContent}
              onChange={e => setNewNoteContent(e.target.value)}
              className="min-h-[80px] w-full border-[#ddd5cb] bg-white text-sm focus-visible:ring-desert-sand-300"
              placeholder="Add a note..."
              autoFocus
            />
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={resetForm} disabled={isCreating}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleAddNote} disabled={isCreating}>
                {isCreating ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
        )}
        {!notes.length && !isAdding && <p className="text-sm font-medium text-gray-500">No notes added yet</p>}
      </div>
    </div>
  );
};
